import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Info } from 'lucide-react';
import { usePayableEmployees } from '@/composables/payroll/useEmployees';
import { usePayslips, usePayslipPreview } from '@/composables/payroll/usePayslips';
import { useDebounce } from '@/composables/shared/useDebounce';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DecimalInput } from '@/components/forms/DecimalInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatCurrency } from '@/lib/formatters';
import { MONTH_OPTIONS, type PreviewLine } from '@/types/payroll';

const YEARS = (() => {
  const now = new Date().getFullYear();
  return Array.from({ length: 6 }, (_, i) => String(now + 1 - i));
})();

export function PayslipGeneratePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { generate } = usePayslips();
  const { preview, data, error, isComputing } = usePayslipPreview();

  // Only active employees with a salary structure can be paid.
  const { employees } = usePayableEmployees();

  const today = new Date();
  // Default to last month — payroll is run after the period closes.
  const defaultMonth = today.getUTCMonth() === 0 ? 12 : today.getUTCMonth();
  const defaultYear = today.getUTCMonth() === 0 ? today.getUTCFullYear() - 1 : today.getUTCFullYear();

  const [employeeId, setEmployeeId] = useState(searchParams.get('employeeId') ?? '');
  const [month, setMonth] = useState(String(defaultMonth));
  const [year, setYear] = useState(String(defaultYear));
  const [paidDays, setPaidDays] = useState('');
  const [payDate, setPayDate] = useState('');
  const [notes, setNotes] = useState('');

  const debouncedPaidDays = useDebounce(paidDays, 350);

  useEffect(() => {
    if (!employeeId) return;
    preview.mutate({
      employeeId,
      periodMonth: Number(month),
      periodYear: Number(year),
      ...(debouncedPaidDays !== '' ? { paidDays: Number(debouncedPaidDays) } : {}),
      ...(payDate ? { payDate } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, month, year, debouncedPaidDays, payDate]);

  const currency = data?.currency ?? 'INR';
  const fmt = (n: number) => formatCurrency(n, currency);
  const blocked = !!data?.existingPayslip;

  const onGenerate = () => {
    generate.mutate(
      {
        employeeId,
        periodMonth: Number(month),
        periodYear: Number(year),
        ...(paidDays !== '' ? { paidDays: Number(paidDays) } : {}),
        ...(payDate ? { payDate } : {}),
        ...(notes ? { notes } : {}),
      },
      { onSuccess: (res) => navigate(`/people/payslips/${res.data.id}`) },
    );
  };

  const employeeOptions = useMemo(
    () => employees.map((e) => ({
      value: e.id,
      label: `${e.firstName} ${e.lastName} (${e.employeeCode})`,
    })),
    [employees],
  );

  return (
    <PageContainer backTo
      title="Generate Payslip"
      description="Figures are computed from the salary structure in force at the end of the period, then frozen onto the payslip."
      
    >
      <div className="grid gap-6 lg:grid-cols-5">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader><CardTitle className="text-base">Period</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Employee <span className="text-destructive">*</span></Label>
                <Select value={employeeId} onValueChange={setEmployeeId}>
                  <SelectTrigger><SelectValue placeholder="Select an employee" /></SelectTrigger>
                  <SelectContent>
                    {employeeOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Only active employees with a salary structure are listed.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Month</Label>
                  <Select value={month} onValueChange={setMonth}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTH_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Year</Label>
                  <Select value={year} onValueChange={setYear}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="paidDays">Paid days</Label>
                <DecimalInput
                  id="paidDays" value={paidDays}
                  placeholder={data ? String(data.totalDays) : 'full month'}
                  onChange={(e) => setPaidDays(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank for a full month. Lowering it prorates every component except
                  those marked "no proration".
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="payDate">Pay date</Label>
                <Input id="payDate" type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
                <p className="text-xs text-muted-foreground">Defaults to the last day of the period.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Input id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Unpaid leave 21–27 Sep" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-3">
          <Card>
            <CardHeader><CardTitle className="text-base">Preview</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {!employeeId && (
                <p className="py-10 text-center text-sm text-muted-foreground">Select an employee to preview their payslip.</p>
              )}

              {error && (
                <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                  <p className="text-xs text-destructive">{error}</p>
                </div>
              )}

              {blocked && (
                <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    {data?.existingPayslip?.payslipNumber} ({data?.existingPayslip?.status}) already covers this
                    month.{' '}
                    {data?.existingPayslip?.status === 'DRAFT'
                      ? 'Edit or delete that draft instead of issuing another.'
                      : 'Open it and use Correct to issue a replacement — that keeps both documents linked, which cancelling does not.'}
                  </p>
                </div>
              )}

              {data && !error && (
                <>
                  <div className="grid gap-3 sm:grid-cols-4">
                    <Stat label="Days in period" value={String(data.totalDays)} />
                    <Stat label="Paid days" value={String(data.paidDays)} />
                    <Stat label="Loss of pay" value={String(data.lopDays)} highlight={data.lopDays > 0} />
                    <Stat label="Net pay" value={fmt(data.netPay)} strong />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <LineTable title="Earnings" lines={data.earnings} total={data.grossEarnings} totalLabel="Gross" fmt={fmt} showFull={data.lopDays > 0} />
                    <LineTable title="Deductions" lines={data.deductions} total={data.totalDeductions} totalLabel="Total" fmt={fmt} showFull={data.lopDays > 0} negative />
                  </div>

                  {data.employerContribs.length > 0 && (
                    <LineTable title="Employer contributions" lines={data.employerContribs} total={data.employerContributions} totalLabel="Total" fmt={fmt} showFull={data.lopDays > 0} />
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
            <Button
              disabled={!employeeId || !data || !!error || blocked || isComputing || generate.isPending}
              onClick={onGenerate}
            >
              {generate.isPending ? 'Generating…' : 'Generate payslip'}
            </Button>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}

function Stat({ label, value, strong, highlight }: { label: string; value: string; strong?: boolean; highlight?: boolean }) {
  return (
    <div className={`rounded-md border p-3 ${strong ? 'border-primary/40 bg-primary/5' : ''}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`mt-0.5 tabular-nums ${strong ? 'text-lg font-semibold' : 'text-base'} ${highlight ? 'text-amber-600 dark:text-amber-400' : ''}`}>
        {value}
      </p>
    </div>
  );
}

function LineTable({
  title, lines, total, totalLabel, fmt, showFull, negative,
}: {
  title: string;
  lines: PreviewLine[];
  total: number;
  totalLabel: string;
  fmt: (n: number) => string;
  showFull: boolean;
  negative?: boolean;
}) {
  return (
    <div className="rounded-md border">
      <div className="border-b bg-muted/40 px-3 py-2">
        <p className="text-xs font-semibold uppercase tracking-wider">{title}</p>
      </div>
      <div className="divide-y">
        {lines.length === 0 && <p className="px-3 py-2 text-sm text-muted-foreground">None</p>}
        {lines.map((l) => (
          <div key={l.code} className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm">
            <span className="min-w-0 truncate text-muted-foreground">
              {l.label}
              {l.isStatutory && <span className="ml-1 text-[10px] uppercase tracking-wide">stat</span>}
            </span>
            <span className="flex shrink-0 items-baseline gap-2">
              {/* Only meaningful once loss of pay has actually reduced something. */}
              {showFull && l.fullAmount !== l.amount && (
                <span className="text-xs text-muted-foreground line-through">{fmt(l.fullAmount)}</span>
              )}
              <span className="tabular-nums">{negative ? '− ' : ''}{fmt(l.amount)}</span>
            </span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between border-t px-3 py-2 text-sm font-semibold">
        <span>{totalLabel}</span>
        <span className="tabular-nums">{negative ? '− ' : ''}{fmt(total)}</span>
      </div>
    </div>
  );
}
